import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  PrimaryButton,
  SecondaryButton,
  Modal,
  DropdownIndicator,
  customStyles,
} from 'components';
import Select from 'react-select';
import RangeSlider from '../../EditProcess/RangeSlider';
import {useParams} from 'react-router-dom';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {CreditCheckContext} from '../../../../../helpers/context/CreditCheckContext';
import {fetchFlow, updateFlow} from '../../../../../requests/queries/flows';
import {toast} from 'react-toastify';

const retentionPeriodArray = [
  {value: 3, label: '3 Months'},
  {value: 6, label: '6 Months'},
  {value: 9, label: '9 Months'},
  {value: 12, label: '12 Months'},
];

function EditDecision({open, setOpen}) {
  const {slug} = useParams();
  const queryClient = useQueryClient();
  const [
    {min_transactions_required, max_transactions_analyzed},
    setDecisionData,
  ] = useState({
    min_transactions_required: 3,
    max_transactions_analyzed: 1000,
  });
  const [retentionPeriod, setRetentionPeriod] = useState(null);

  const {
    set: {setRangeValue},
    get: {rangeValue = [25, 75]},
  } = useContext(CreditCheckContext);
  const {data: flowData, isLoading} = useQuery(['e-flow', slug], () =>
    fetchFlow(slug),
  );
  const currentFlowData = useMemo(
    () => !isLoading && flowData && flowData?.entity,
    [flowData, isLoading],
  );

  const {mutate: updateCurrentFLow, isLoading: updateLoading} = useMutation(
    'update-flow',
    updateFlow,
  );

  const editRuleDecision = useCallback(async () => {
    try {
      const data = {
        name: currentFlowData?.name,
        webhook: currentFlowData?.webhook || ' ',
        notification_emails: currentFlowData?.notification_emails || [' '],
        c_webhook: currentFlowData?.convoy_url || ' ',
        historical_config: {
          retention_period_months: Number(retentionPeriod?.value) || 6,
          min_transactions_required: Number(min_transactions_required) || 3,
          max_transactions_analyzed: Number(max_transactions_analyzed) || 1000,
          include_fields: [
            'amount',
            'currency',
            'type',
            'outcome',
            'risk_score',
          ],
        },
        outcome_ranges: {
          block: {
            min: rangeValue[1] + 1,
            max: 100,
          },
          review: {
            min: rangeValue[0] + 1,
            max: rangeValue[1],
          },
          allow: {
            min: 0,
            max: rangeValue[0],
          },
        },
      };

      await updateCurrentFLow(
        {
          id: slug,
          data: {...data},
        },
        {
          onSuccess: () => {
            queryClient?.invalidateQueries();
            toast.success('Your decisions has been saved');
            setOpen(false);
          },
          onError: error => {
            toast.error(error?.response?.data?.error);
          },
        },
      );
    } catch (e) {
      console.log(e);
    }
  }, [
    currentFlowData?.name,
    currentFlowData?.webhook,
    currentFlowData?.notification_emails,
    currentFlowData?.convoy_url,
    retentionPeriod?.value,
    min_transactions_required,
    max_transactions_analyzed,
    rangeValue,
    updateCurrentFLow,
    slug,
    queryClient,
    setOpen,
  ]);

  useEffect(() => {
    setRangeValue([
      currentFlowData?.outcome_ranges?.allow?.max || 25,
      currentFlowData?.outcome_ranges?.review?.max || 75,
    ]);
    setRetentionPeriod(
      retentionPeriodArray.find(
        r =>
          r.value ===
          currentFlowData?.historical_config?.retention_period_months,
      ),
    );
    setDecisionData({
      min_transactions_required:
        currentFlowData?.historical_config?.min_transactions_required,
      max_transactions_analyzed:
        currentFlowData?.historical_config?.max_transactions_analyzed,
    });
  }, [
    currentFlowData?.historical_config?.min_transactions_required,
    currentFlowData?.historical_config?.max_transactions_analyzed,
    currentFlowData?.historical_config?.retention_period_months,
    currentFlowData?.outcome_ranges?.allow?.max,
    currentFlowData?.outcome_ranges?.review?.max,
    setRangeValue,
  ]);

  const handleReset = useCallback(() => {
    setRetentionPeriod({});
    setRangeValue([25, 75]);
    setDecisionData({
      min_transactions_required: 0,
      max_transactions_analyzed: 0,
    });
  }, [setRangeValue]);

  return (
    <Modal
      show={open}
      onClose={() => setOpen(false)}
      extraModalClasses="max-w-[480px] overflow-y-auto h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg absolute bottom-0 sm:relative lookup-modal"
      modalPosition="justify-center sm:justify-end"
      modalTitle="Edit Decision"
    >
      <div className="px-6 py-4 text-start rounded-[4px] space-y-4">
        <div>
          <p className="mb-2 text-body text-sm text-start">Status</p>
          <p className="text-body text-[11px] text-start">
            Set a percentage<span className="text-[#8E90A9]">(%)</span> value (
            Move sliders to change)
          </p>
        </div>

        {/* Range Slider */}
        <div className="relative !mt-8">
          <div className="absolute bottom-[2px] left-[-6px] h-[12px] z-[9] w-[12px] flex items-center bg-[#D2D2D2] rounded-full aspect-square cursor-not-allowed" />
          <div className="cursor-not-allowed bg-[#4A878D] absolute bottom-[19px] -left-[11px] px-[8px] py-[3px] text-white text-[8px] rounded after:content-[''] after:absolute after:block after:w-0 after:h-0 after:border-l-transparent after:border-l-4 after:border-b-4 after:border-r-transparent after:border-r-4 after:rotate-180 after:left-1/2 after:-translate-x-1/2 after:-bottom-[3px] after:border-b-[#4A878D]">
            0
          </div>
          <RangeSlider
            onChange={setRangeValue}
            className="w-full h-4"
            defaultValue={rangeValue}
          />
          <div className="absolute bottom-[2px] right-[-6px] h-[12px] w-[12px] flex items-center bg-[#D2D2D2] rounded-full aspect-square cursor-not-allowed" />
          <div className="cursor-not-allowed bg-status-404 absolute bottom-[19px] right-[-9px] p-[2px] text-white text-[8px] rounded after:content-[''] after:absolute after:block after:w-0 after:h-0 after:border-l-transparent after:border-l-4 after:border-b-4 after:border-r-transparent after:border-r-4 after:rotate-180 after:left-1/2 after:-translate-x-1/2 after:-bottom-[3px] after:border-b-status-404">
            100
          </div>
        </div>

        <div className="w-full h-[1px] bg-[#E6E6E6]" />
        <div>
          <p className="mb-2 text-body text-sm text-start">
            Historical configuration
          </p>
          <p className="text-body text-[11px] text-start">
            Set a percentage<span className="text-[#8E90A9]">(%)</span> value (
            Move sliders to change){' '}
          </p>
        </div>
        <div className="w-full h-[1px] bg-[#E6E6E6]" />

        <div>
          <label className="block mb-1">Retention period</label>
          <Select
            options={retentionPeriodArray}
            components={{DropdownIndicator}}
            classNamePrefix="react-select"
            styles={customStyles}
            placeholder="Choose period"
            isClearable
            onChange={data => setRetentionPeriod(data)}
            defaultValue={retentionPeriod}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block mb-1">Min. transaction required</label>
            <input
              value={min_transactions_required}
              onChange={e =>
                setDecisionData(prev => ({
                  ...prev,
                  min_transactions_required: +e.target.value,
                }))
              }
              type="text"
              placeholder="Input count"
              className="text-sm"
            />
          </div>
          <div>
            <label className="block mb-1">Max transactions analyzed</label>
            <input
              value={max_transactions_analyzed}
              onChange={e =>
                setDecisionData(prev => ({
                  ...prev,
                  max_transactions_analyzed: +e.target.value,
                }))
              }
              type="text"
              placeholder="Input count"
              className="text-sm"
            />
          </div>
        </div>
      </div>

      <div className="w-full mt-6 px-6 py-[14px] grid md:grid-cols-2 gap-6 border-[0.5px] border-transparent border-t-[#EFEFEF]">
        <PrimaryButton
          buttonText="Apply"
          className="w-full !h-10"
          onClick={editRuleDecision}
          loading={updateLoading}
        />
        <SecondaryButton
          onClick={handleReset}
          buttonText="Reset"
          className="whitespace-nowrap !h-10"
        />
      </div>
    </Modal>
  );
}

export default EditDecision;
